import React from 'react';
import { ShoppingCart, Truck, Home, Activity, List, DollarSign, Heart } from 'react-feather';

function ExpensesGrid({ formData, onDataChange }) {
  
  // Helper for nested state updates
  const handleNestedChange = (section, field, value) => {
    const syntheticEvent = {
      target: {
        name: `${section}.${field}`,
        value: value,
        type: typeof value === 'boolean' ? 'checkbox' : 'text',
        checked: true ? value : undefined
      }
    };
    onDataChange(syntheticEvent);
  };

  const handleRadioChange = (section, field, value) => {
    const syntheticEvent = {
      target: {
        name: `${section}.${field}`,
        value: value === 'true',
        type: 'radio'
      }
    };
    onDataChange(syntheticEvent);
  };

  const vehicle = formData?.businessVehicle || {};
  const homeOffice = formData?.homeOffice || {};

  return (
    <div className="form-section">
      <div className="content-box-header">
        <h2><ShoppingCart size={20} className="icon-orange" /> Expenses</h2>
      </div>

      {/* =================================================================================
          SECTION: BUSINESS EXPENSE DETAILS
         ================================================================================= */}
      <div className="dependent-card">
        <h3 style={{ color: '#554433ff', marginBottom: '1.5rem', fontSize: '1.1rem', borderBottom: '1px solid #f0e8e2ff', paddingBottom: '0.5rem' }}>
          Business Expenses
        </h3>
        
        {/* Manual Override for Business */}
        <div className="form-group">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <DollarSign size={16} className="icon-blue" />
                <label style={{ fontWeight: 600, color: '#554633ff', margin: 0 }}>Total Business Expenses</label>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#8b7864ff', marginBottom: '0.75rem' }}>
                Enter the total amount of expenses for your business (Advertising, Supplies, Utilities, etc.).
            </p>
            <div className="input-with-prefix">
                <span className="prefix">$</span>
                <input 
                    type="number" 
                    name="manualBusinessExpenses" 
                    value={formData?.manualBusinessExpenses ?? ''} 
                    onChange={onDataChange} 
                    placeholder="0" 
                />
            </div>
        </div>

        {/* Vehicle Info */}
        <div style={{ marginTop: '2rem' }}>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem', marginBottom: '1rem' }}>
                <Truck size={18} className="icon-blue" /> Business Vehicle Information
            </h4>
            <div className="form-group" style={{ marginLeft: '1rem', borderLeft: '3px solid #f0e9e2ff', paddingLeft: '1rem' }}>
                <div className="form-row">
                    <div className="form-group">
                        <label>Date placed in service</label>
                        <input type="date" value={vehicle.inServiceDate} onChange={(e) => handleNestedChange('businessVehicle', 'inServiceDate', e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>Total miles driven</label>
                        <input type="number" value={vehicle.totalMiles} onChange={(e) => handleNestedChange('businessVehicle', 'totalMiles', e.target.value)} placeholder="0" />
                    </div>
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label>Business miles</label>
                        <input type="number" value={vehicle.businessMiles} onChange={(e) => handleNestedChange('businessVehicle', 'businessMiles', e.target.value)} placeholder="0" />
                    </div>
                    <div className="form-group">
                        <label>Commuting miles</label>
                        <input type="number" value={vehicle.commutingMiles} onChange={(e) => handleNestedChange('businessVehicle', 'commutingMiles', e.target.value)} placeholder="0" />
                    </div>
                </div>
                <div className="form-group" style={{ marginTop: '1rem' }}>
                    <label>Do you have evidence to support your deduction?</label>
                    <div className="radio-group">
                        <input type="radio" id="evidenceNo" name="businessVehicle.hasEvidence" value="false" checked={vehicle.hasEvidence === false} onChange={(e) => handleRadioChange('businessVehicle', 'hasEvidence', e.target.value)} />
                        <label htmlFor="evidenceNo">No</label>
                        <input type="radio" id="evidenceYes" name="businessVehicle.hasEvidence" value="true" checked={vehicle.hasEvidence === true} onChange={(e) => handleRadioChange('businessVehicle', 'hasEvidence', e.target.value)} />
                        <label htmlFor="evidenceYes">Yes</label>
                    </div>
                </div>
            </div>
        </div>

        {/* Home Office */}
        <div style={{ marginTop: '2rem' }}>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem', marginBottom: '1rem' }}>
                <Home size={18} className="icon-blue" /> Business Use of Home
            </h4>
            <div className="form-group" style={{ marginLeft: '1rem', borderLeft: '3px solid #f0eae2ff', paddingLeft: '1rem' }}>
                <div className="form-row">
                    <div className="form-group">
                        <label>Area used exclusively for business (sq. ft.)</label>
                        <input type="number" value={homeOffice.areaUsed} onChange={(e) => handleNestedChange('homeOffice', 'areaUsed', e.target.value)} placeholder="0" />
                    </div>
                    <div className="form-group">
                        <label>Total area of home (sq. ft.)</label>
                        <input type="number" value={homeOffice.totalArea} onChange={(e) => handleNestedChange('homeOffice', 'totalArea', e.target.value)} placeholder="0" />
                    </div>
                </div>
            </div>
        </div>
      </div>

      {/* =================================================================================
          SECTION: PERSONAL EXPENSE SUMMARIES (Schedule A)
         ================================================================================= */}
      <div className="dependent-card" style={{ marginTop: '2rem' }}>
        <h3 style={{ color: '#554433ff', marginBottom: '1.5rem', fontSize: '1.1rem', borderBottom: '1px solid #f0e9e2ff', paddingBottom: '0.5rem' }}>
          Personal Expenses
        </h3>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Enter your itemized deductions here. We will automatically compare these against the Standard Deduction to see which gives you the better tax outcome.
        </p>

        <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontSize: '1rem' }}>
                <Activity size={18} className="icon-blue" /> Medical & Dental
            </h4>
            <div className="form-group">
                <label>Total Medical Expenses</label>
                <div className="input-with-prefix">
                    <span className="prefix">$</span>
                    <input type="number" name="manualMedicalExpenses" value={formData.manualMedicalExpenses ?? ''} onChange={onDataChange} placeholder="0" />
                </div>
            </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontSize: '1rem' }}>
                <List size={18} className="icon-blue" /> Taxes You Paid
            </h4>
            <div className="form-row">
                <div className="form-group">
                    <label>State & Local Income Taxes</label>
                    <input type="number" name="manualStateTaxes" value={formData.manualStateTaxes ?? ''} onChange={onDataChange} placeholder="0" />
                </div>
                <div className="form-group">
                    <label>Real Estate Taxes</label>
                    <input type="number" name="manualRealEstateTaxes" value={formData.manualRealEstateTaxes ?? ''} onChange={onDataChange} placeholder="0" />
                </div>
            </div>
            <div className="form-row">
                <div className="form-group">
                    <label>Personal Property Taxes</label>
                    <input type="number" name="manualPersonalPropertyTaxes" value={formData.manualPersonalPropertyTaxes ?? ''} onChange={onDataChange} placeholder="0" />
                </div>
                <div className="form-group">
                    <label>General Sales Tax (if electing)</label>
                    <input type="number" name="manualGeneralSalesTax" value={formData.manualGeneralSalesTax ?? ''} onChange={onDataChange} placeholder="0" />
                </div>
            </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontSize: '1rem' }}>
                <Home size={18} className="icon-blue" /> Interest You Paid
            </h4>
            <div className="form-group">
                <label>Mortgage Interest (Form 1098)</label>
                <div className="input-with-prefix">
                    <span className="prefix">$</span>
                    <input type="number" name="manualMortgageInterest" value={formData.manualMortgageInterest ?? ''} onChange={onDataChange} placeholder="0" />
                </div>
            </div>
        </div>

        <div>
            <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontSize: '1rem' }}>
                <Heart size={18} className="icon-blue" /> Gifts to Charity
            </h4>
            <div className="form-row">
                <div className="form-group">
                    <label>Gifts by Cash or Check</label>
                    <input type="number" name="manualCharityCash" value={formData.manualCharityCash ?? ''} onChange={onDataChange} placeholder="0" />
                </div>
                <div className="form-group">
                    <label>Gifts other than Cash</label>
                    <input type="number" name="manualCharityNonCash" value={formData.manualCharityNonCash ?? ''} onChange={onDataChange} placeholder="0" />
                </div>
            </div>
        </div>

      </div>
    </div>
  );
}

export default ExpensesGrid;